import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Card, Col, Form, Modal, ProgressBar, Row, Table } from 'react-bootstrap';
import { FaAngleDown, FaAngleUp, FaArchive, FaBuilding, FaCheckCircle, FaDownload, FaEdit, FaEye, FaFileInvoice, FaFilePdf, FaPlusCircle, FaStar, FaSync, FaTimesCircle, FaUpload, FaUser } from 'react-icons/fa';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import BulkEditCareRecipientsModal from '../components/BulkEditCareRecipientsModal';
import CustomerSiteList from '../components/CustomerSiteList';
import CustomerTenantList from '../components/CustomerTenantList';
import LoadingSpinner, { LoadingButton } from '../components/LoadingSpinner';
import TableSkeleton from '../components/TableSkeleton';
import SiteSelector from '../components/SiteSelector';
import PermissionGuard from '../components/PermissionGuard';
import { API_BASE_URL } from '../config';
import { useTenant } from '../context/TenantContext';
import { usePermissions } from '../context/PermissionsContext';
import { PERMISSIONS } from '../constants/permissions';
import './CareRecipients.css';

function CareRecipients() {
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerTenants, setCustomerTenants] = useState([]);
    const [customerSites, setCustomerSites] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [pdfFile, setPdfFile] = useState(null);
    const [selectedDate, setSelectedDate] = useState("");
    const [formErrors, setFormErrors] = useState({});
    const [organisationalSites, setOrganisationalSites] = useState([]);
    const [sites, setSites] = useState([]);
    const [allTenants, setAllTenants] = useState([]);
    const [customerCareTypes, setCustomerCareTypes] = useState([]);
    const [searchTerm, setSearchTerm] = useState(() => sessionStorage.getItem('careRecipients_search') || "");
    const [sortKey, setSortKey] = useState("Name");
    const [sortOrder, setSortOrder] = useState("asc");
    const [showEditModal, setShowEditModal] = useState(false);
    const [showArchiveModal, setShowArchiveModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [recipientToArchive, setRecipientToArchive] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [showBulkEditModal, setShowBulkEditModal] = useState(false);
    const [isBulkEditing, setIsBulkEditing] = useState(false);
    const [bulkEditProgress, setBulkEditProgress] = useState({ done: 0, total: 0 });
    const [activeSiteFilter, setActiveSiteFilter] = useState('all');

    // Multi-step form state for new customer
    const [createStep, setCreateStep] = useState(1);
    const [newCustomer, setNewCustomer] = useState({
        Name: '',
        Surname: '',
        IDNo: '',
        crm_id: '',
        room_number: '',
        Status: '',
        CustomerCareTypeID: '',
        Discount: '',
        mod_shared: false,
        mod_immobile: false,
        mod_estate: false
    });
    // New tenant/site selection state
    const [selectedTenantIds, setSelectedTenantIds] = useState([]);
    const [selectedTenantsData, setSelectedTenantsData] = useState([]); // Store full tenant objects for name display
    const [primaryTenantId, setPrimaryTenantId] = useState(null);
    const [tenantSiteSelections, setTenantSiteSelections] = useState({}); // { tenantId: [siteIds] }
    const [existingPerson, setExistingPerson] = useState(null);
    const [personLookupStatus, setPersonLookupStatus] = useState('');
    const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);
    // New state for assessment functionality
    const [showAssessmentModal, setShowAssessmentModal] = useState(false);
    const [selectedCustomerForAssessment, setSelectedCustomerForAssessment] = useState(null);
    const [assessmentFile, setAssessmentFile] = useState(null);
    const [uploadingAssessment, setUploadingAssessment] = useState(false);
    const [assessmentExists, setAssessmentExists] = useState({});
    const [isActive, setIsActive] = useState(false);
    // Start true when landing on this page so we never flash an empty state before the first fetch.
    const [isLoading, setIsLoading] = useState(() => typeof window !== 'undefined' && window.location.pathname === '/care-recipients');
    const [isLoadingAdditional, setIsLoadingAdditional] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [quickQuoteLoadingKey, setQuickQuoteLoadingKey] = useState(null);
    const [quickQuoteExplainer, setQuickQuoteExplainer] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { currentTenant, isLoading: isTenantLoading } = useTenant();
    const { hasPermission, hasRole } = usePermissions();
    const canCreateRecipients = hasRole('Admin') || hasRole('Manager');
    const canCreateForCurrentTenant = Boolean(currentTenant?.id)
        && canCreateRecipients
        && hasPermission(PERMISSIONS.CLIENT_CREATE);

    // Add refs for timeout management
    const assessmentTimeoutRef = useRef(null);
    const abortControllerRef = useRef(null);
    // Ignore stale responses when tenant/focus triggers overlapping fetches
    const fetchGenerationRef = useRef(0);
    const isCreatingCustomerRef = useRef(false);

    // Refs to always hold current sort state so the fetch effect can re-apply it
    const sortKeyRef = useRef(sortKey);
    const sortOrderRef = useRef(sortOrder);
    useEffect(() => {
        sortKeyRef.current = sortKey;
        sortOrderRef.current = sortOrder;
    }, [sortKey, sortOrder]);

    // Helper function to create fetch with timeout
    const fetchWithTimeout = useCallback(async (url, options = {}, timeout = 10000) => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        try {
            const response = await fetchWithAuth(url, {
                ...options,
                signal: controller.signal
            });
            clearTimeout(timeoutId);
            return response;
        } catch (error) {
            clearTimeout(timeoutId);
            if (error.name === 'AbortError') {
                throw new Error('Request timeout');
            }
            throw error;
        }
    }, []);

    // Effect to handle component activation
    useEffect(() => {
        if (location.pathname === '/care-recipients') {
            // Show skeleton immediately so we never briefly render "No service recipients found"
            setIsLoading(true);
            setLoadError(null);
            setIsActive(true);
        } else {
            setIsActive(false);
            // Cancel any pending requests
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            if (assessmentTimeoutRef.current) {
                clearTimeout(assessmentTimeoutRef.current);
            }
            // Invalidate in-flight fetches so they cannot write after we leave
            fetchGenerationRef.current += 1;
            // Reset state when leaving the page
            setCustomers([]);
            setCustomerCareTypes([]);
            setOrganisationalSites([]);
            setSites([]);
            setAssessmentExists({});
            setLoadError(null);
            setActiveSiteFilter('all');
        }
    }, [location.pathname]);

    // Effect to handle window focus events
    useEffect(() => {
        let focusTimeout;
        const handleFocus = () => {
            if (location.pathname === '/care-recipients' && isActive) {
                // Add a small delay to prevent too frequent refreshes
                clearTimeout(focusTimeout);
                focusTimeout = setTimeout(() => {
                    refreshData();
                }, 500);
            }
        };

        window.addEventListener('focus', handleFocus);
        return () => {
            window.removeEventListener('focus', handleFocus);
            clearTimeout(focusTimeout);
        };
    }, [location.pathname, isActive]);

    // Initial load - service recipients + sites (sites needed for labels / access filtering)
    useEffect(() => {
        if (!isActive) return;
        // Wait for tenant context so client-side tenant scoping is stable on first paint
        if (isTenantLoading) return;

        const generation = ++fetchGenerationRef.current;
        setIsLoading(true);
        setLoadError(null);

        const fetchCareRecipients = async () => {
            try {
                // Care-recipients with sites is heavier; allow longer than the default 10s timeout.
                // Fetch sites in parallel but don't fail the whole page if /sites errors.
                const recipientsPromise = fetchWithTimeout(
                    `${API_BASE_URL}/care-recipients?include_sites=true`,
                    {},
                    30000
                );
                const sitesPromise = fetchWithTimeout(`${API_BASE_URL}/sites`, {}, 15000).catch(err => {
                    console.error('Error fetching sites alongside recipients:', err);
                    return null;
                });

                const [recipientsResponse, sitesResponse] = await Promise.all([
                    recipientsPromise,
                    sitesPromise,
                ]);

                if (generation !== fetchGenerationRef.current) return;

                if (!recipientsResponse.ok) {
                    console.error(`HTTP error! Status: ${recipientsResponse.status}`);
                    setLoadError('Failed to load service recipients. Please try again.');
                    return;
                }

                const data = await recipientsResponse.json();
                if (generation !== fetchGenerationRef.current) return;

                // Transform snake_case field names from Supabase to PascalCase for frontend compatibility
                // Handle both legacy fields and new multi-tenant/multi-site arrays
                const transformedData = data.map(customer => ({
                    CustomerID: customer.customer_id,
                    Name: customer.name,
                    Surname: customer.surname,
                    IDNo: customer.id_no,
                    crm_id: customer.crm_id,
                    room_number: customer.room_number,
                    Status: customer.status,
                    CustomerCareTypeID: customer.customer_care_type_id,
                    Discount: customer.discount,
                    OrganisationalSiteID: customer.organisational_site_id,
                    // Legacy single tenant/site fields (for backwards compatibility)
                    tenant_id: customer.tenant_id,
                    site_id: customer.site_id,
                    mod_shared: customer.mod_shared,
                    mod_immobile: customer.mod_immobile,
                    mod_estate: customer.mod_estate,
                    // New multi-tenant/multi-site arrays from include_sites=true
                    tenants: customer.tenants || [],
                    sites: customer.sites || [],
                    has_assessment: Boolean(customer.has_assessment)
                }));

                // Backend now handles tenant filtering via user's allowed sites
                // No need to filter by currentTenant here - the API returns only allowed customers
                const tenantFilteredData = transformedData;

                // Filter out archived recipients (Status === "ARCHIVED")
                const filteredData = tenantFilteredData.filter(customer => customer.Status !== "ARCHIVED");

                // Re-apply current sort so search/filter state is preserved after save
                const currentSortKey = sortKeyRef.current;
                const currentSortOrder = sortOrderRef.current;
                const sortedData = [...filteredData].sort((a, b) => {
                    if (currentSortKey === 'Discount') {
                        const numA = parseFloat(a[currentSortKey] || 0);
                        const numB = parseFloat(b[currentSortKey] || 0);
                        return currentSortOrder === 'asc' ? numA - numB : numB - numA;
                    }
                    const compA = typeof a[currentSortKey] === 'string' ? a[currentSortKey].toLowerCase() : (a[currentSortKey] ?? '');
                    const compB = typeof b[currentSortKey] === 'string' ? b[currentSortKey].toLowerCase() : (b[currentSortKey] ?? '');
                    if (compA > compB) return currentSortOrder === 'asc' ? 1 : -1;
                    if (compA < compB) return currentSortOrder === 'asc' ? -1 : 1;
                    return 0;
                });
                setCustomers(sortedData);
                setAssessmentExists(Object.fromEntries(
                    sortedData.map((customer) => [customer.CustomerID, Boolean(customer.has_assessment)])
                ));

                // Apply sites as soon as recipients load so tabs never flash as "Unassigned"
                if (sitesResponse?.ok) {
                    const sitesData = await sitesResponse.json();
                    if (generation !== fetchGenerationRef.current) return;
                    const transformedSites = sitesData.map(item => ({
                        SiteID: item.site_id,
                        SiteName: item.name,
                        TenantID: item.tenant_id,
                        TenantName: item.tenant_name
                    }));
                    setSites(transformedSites);
                }
            } catch (error) {
                if (generation !== fetchGenerationRef.current) return;
                console.error('Error fetching service recipients:', error);
                const message = error?.message === 'Request timeout'
                    ? 'Loading timed out. Please refresh and try again.'
                    : 'Failed to load service recipients. Please try again.';
                setLoadError(message);
            } finally {
                if (generation === fetchGenerationRef.current) {
                    setIsLoading(false);
                }
            }
        };

        fetchCareRecipients();
    }, [isActive, location.pathname, fetchWithTimeout, currentTenant?.id, isTenantLoading]);

    // Load additional information when modal opens or service recipients are loaded
    // (care types / org sites / tenants — sites are loaded with recipients above)
    useEffect(() => {
        if (!(showModal || customers.length > 0)) return;

        let cancelled = false;
        setIsLoadingAdditional(true);

        const fetchAdditionalData = async () => {
            try {
                const [careTypesResponse, orgSitesResponse, tenantsResponse] = await Promise.all([
                    fetchWithTimeout(`${API_BASE_URL}/customerCareTypes`),
                    fetchWithTimeout(`${API_BASE_URL}/organisationalSites`),
                    fetchWithTimeout(`${API_BASE_URL}/my-tenants`),
                ]);

                if (cancelled) return;

                if (careTypesResponse.ok) {
                    const careTypesData = await careTypesResponse.json();
                    const transformedCareTypes = careTypesData.map(item => ({
                        CareTypeID: item.care_type_id,
                        Name: item.name
                    }));
                    setCustomerCareTypes(transformedCareTypes);
                }

                if (orgSitesResponse.ok) {
                    const orgSitesData = await orgSitesResponse.json();
                    const transformedOrgSites = orgSitesData.map(item => ({
                        OrganisationalSiteID: item.organisational_site_id,
                        OrganisationalSiteName: item.organisational_site_name
                    }));
                    setOrganisationalSites(transformedOrgSites);
                }

                if (tenantsResponse.ok) {
                    const tenantsData = await tenantsResponse.json();
                    if (tenantsData.length > 0) {
                        const transformedTenants = tenantsData.map(item => ({
                            TenantID: item.tenant_id || item.id,
                            TenantName: item.tenant_name || item.name,
                            Status: item.status
                        }));
                        setAllTenants(transformedTenants);
                    }
                }
            } catch (error) {
                if (!cancelled) {
                    console.error('Error fetching additional data:', error);
                }
            } finally {
                if (!cancelled) {
                    setIsLoadingAdditional(false);
                }
            }
        };

        fetchAdditionalData();
        return () => {
            cancelled = true;
        };
    }, [customers.length, showModal, fetchWithTimeout]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            if (assessmentTimeoutRef.current) {
                clearTimeout(assessmentTimeoutRef.current);
            }
        };
    }, []);

    const checkAssessmentExists = useCallback(async (customerId) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/assessments/${customerId}/exists`, {}, 15000);
            if (response.ok) {
                const data = await response.json();
                setAssessmentExists(prev => ({
                    ...prev,
                    [customerId]: data.exists
                }));
            }
        } catch (error) {
            if (error?.message !== 'Request timeout') {
                console.error('Error checking assessment existence:', error);
            }
        }
    }, [fetchWithTimeout]);

    const handleAssessmentUpload = async () => {
        if (!assessmentFile || !selectedCustomerForAssessment) return;

        const formData = new FormData();
        formData.append('assessmentFile', assessmentFile);
        formData.append('customerID', selectedCustomerForAssessment.CustomerID);

        setUploadingAssessment(true);
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/assessments/upload`, {
                method: 'POST',
                body: formData
            }, 30000); // 30 second timeout for uploads

            if (response.ok) {
                alert('Assessment uploaded successfully');
                setShowAssessmentModal(false);
                setAssessmentFile(null);
                setSelectedCustomerForAssessment(null);
                // Update the assessment exists state
                setAssessmentExists(prev => ({
                    ...prev,
                    [selectedCustomerForAssessment.CustomerID]: true
                }));
            } else {
                alert('Failed to upload assessment');
            }
        } catch (error) {
            console.error('Error uploading assessment:', error);
            alert('Error uploading assessment');
        } finally {
            setUploadingAssessment(false);
        }
    };

    // Wrapper to check permission before upload
    const handleAssessmentUploadWithPermission = () => {
        if (!hasPermission(PERMISSIONS.ASSESSMENT_CREATE)) {
            alert('You do not have permission to upload assessments.');
            return;
        }
        handleAssessmentUpload();
    };

    const handleAssessmentDownload = async (customerId) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/assessments/${customerId}/download`, {}, 15000); // 15 second timeout for downloads

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `assessment_${customerId}.pdf`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else {
                alert('Failed to download assessment');
            }
        } catch (error) {
            console.error('Error downloading assessment:', error);
            alert('Error downloading assessment');
        }
    };

    const handleAssessmentModalOpen = (customer) => {
        setSelectedCustomerForAssessment(customer);
        setShowAssessmentModal(true);
        // Check if assessment exists for this customer
        checkAssessmentExists(customer.CustomerID);
    };

    // Check if user has permission to upload assessment
    const canUploadAssessment = () => {
        return hasPermission(PERMISSIONS.ASSESSMENT_CREATE);
    };

    const handleAssessmentModalClose = () => {
        setShowAssessmentModal(false);
        setSelectedCustomerForAssessment(null);
        setAssessmentFile(null);
    };

    const refreshData = useCallback(() => {
        if (isLoading || isLoadingAdditional) return; // Prevent multiple simultaneous refreshes
        // Cancel any pending requests
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        if (assessmentTimeoutRef.current) {
            clearTimeout(assessmentTimeoutRef.current);
        }
        setIsActive(false);
        setTimeout(() => setIsActive(true), 100);
    }, [isLoading, isLoadingAdditional]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type === 'application/pdf') {
            setAssessmentFile(file);
        } else {
            alert('Please select a PDF file');
            e.target.value = null;
        }
    };

    const handleSiteChange = (e) => {
        const selectedID = e.target.value;
        setSelectedCustomer((prevState) => ({
            ...prevState,
            OrganisationalSiteID: selectedID
        }));
    };

    const handleCareTypeChange = (e) => {
        const selectedID = e.target.value;
        setSelectedCustomer((prevState) => ({
            ...prevState,
            CustomerCareTypeID: selectedID
        }));
    };

    const handleSort = (key) => {
        const order = sortKey === key && sortOrder === "asc" ? "desc" : "asc";

        if (sortKey === key && sortOrder === order) return;

        setSortKey(key);
        setSortOrder(order);

        setCustomers((prevCustomers) =>
            [...prevCustomers].sort((a, b) => {
                // Special handling for numeric fields like Discount
                if (key === "Discount") {
                    // Convert to numbers for proper numeric comparison
                    const numA = parseFloat(a[key] || 0);
                    const numB = parseFloat(b[key] || 0);

                    return order === "asc" ? numA - numB : numB - numA;
                } else {
                    const valA = a[key];
                    const valB = b[key];

                    // Check if the values are strings and convert to lowercase for comparison
                    const comparisonA = typeof valA === 'string' ? valA.toLowerCase() : valA;
                    const comparisonB = typeof valB === 'string' ? valB.toLowerCase() : valB;

                    if (comparisonA > comparisonB) {
                        return order === "asc" ? 1 : -1;
                    } else if (comparisonA < comparisonB) {
                        return order === "asc" ? -1 : 1;
                    } else {
                        return 0; // Values are equal
                    }
                }
            })
        );
    };

    const getSortIndicator = (key) => {
        if (sortKey === key) {
            return sortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />;
        }
        return null;
    };

    // Recipients the API returns can span every tenant the user can access.
    // Scope to the selected tenant so hero stats match what the table shows.
    const tenantCustomers = useMemo(() => {
        const list = customers || [];
        if (!currentTenant?.id) return list;
        const tenantId = String(currentTenant.id);
        return list.filter(customer => {
            const customerSites = customer.sites || [];
            if (customerSites.some(site => String(site.tenant_id) === tenantId)) {
                return true;
            }
            const customerTenants = customer.tenants || [];
            if (customerTenants.some(tenant => String(tenant.tenant_id) === tenantId)) {
                return true;
            }
            // Legacy single-tenant field
            return customer.tenant_id != null && String(customer.tenant_id) === tenantId;
        });
    }, [customers, currentTenant?.id]);

    const filteredCustomers = useMemo(() =>
        tenantCustomers.filter(customer =>
            customer.Name?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
            customer.Surname?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
            customer.IDNo?.includes(searchTerm) ||
            customer.crm_id?.includes(searchTerm) ||
            customer.room_number?.toLowerCase().includes(searchTerm?.toLowerCase())
        ),
    [tenantCustomers, searchTerm]);

    // Sites the current user can access (from /api/sites). Used so inaccessible
    // memberships never get grouped under a fake "Unassigned" bucket.
    const allowedSiteIds = useMemo(
        () => new Set((sites || []).map(s => String(s.SiteID))),
        [sites]
    );

    const UNASSIGNED_SITE_KEY = '__unassigned__';

    // Prefer /sites catalog, but fall back to site_name already on each recipient
    // so tabs never flash "Unassigned" while additional data is still loading.
    const siteNameById = useMemo(() => {
        const map = {};
        (sites || []).forEach(s => {
            if (s.SiteID != null && s.SiteName) {
                map[String(s.SiteID)] = s.SiteName;
            }
        });
        (customers || []).forEach(c => {
            (c.sites || []).forEach(site => {
                if (site.site_id != null && site.site_name && !map[String(site.site_id)]) {
                    map[String(site.site_id)] = site.site_name;
                }
            });
        });
        return map;
    }, [sites, customers]);

    const getSiteGroupLabel = (siteId) => {
        if (String(siteId) === UNASSIGNED_SITE_KEY) return 'No site assigned';
        return siteNameById[String(siteId)] || 'Unknown site';
    };

    const groupedCustomers = useMemo(() => filteredCustomers?.reduce((groups, customer) => {
        const customerSites = customer.sites || [];
        const tenantFilteredSites = currentTenant
            ? customerSites.filter(site => String(site.tenant_id) === String(currentTenant.id))
            : customerSites;

        // Prefer only sites the user can access. If the sites list hasn't loaded yet,
        // fall back to the API payload (which should already be access-filtered).
        const accessibleSites = allowedSiteIds.size > 0
            ? tenantFilteredSites.filter(site => allowedSiteIds.has(String(site.site_id)))
            : tenantFilteredSites;

        if (accessibleSites.length > 0) {
            accessibleSites.forEach(site => {
                const siteId = site.site_id;
                if (!groups[siteId]) groups[siteId] = [];
                if (!groups[siteId].some(c => c.CustomerID === customer.CustomerID)) {
                    groups[siteId].push(customer);
                }
            });
        } else if (
            !currentTenant &&
            customer.site_id &&
            (allowedSiteIds.size === 0 || allowedSiteIds.has(String(customer.site_id)))
        ) {
            const siteId = customer.site_id;
            if (!groups[siteId]) groups[siteId] = [];
            groups[siteId].push(customer);
        } else {
            // Tenant-assigned (or in scope) with no accessible sites → Unassigned bucket
            if (!groups[UNASSIGNED_SITE_KEY]) groups[UNASSIGNED_SITE_KEY] = [];
            if (!groups[UNASSIGNED_SITE_KEY].some(c => c.CustomerID === customer.CustomerID)) {
                groups[UNASSIGNED_SITE_KEY].push(customer);
            }
        }
        return groups;
    }, {}), [filteredCustomers, currentTenant, allowedSiteIds]);

    const sortedSiteEntries = useMemo(() => {
        if (!groupedCustomers) return [];
        return Object.entries(groupedCustomers).sort(([siteIdA], [siteIdB]) => {
            if (String(siteIdA) === UNASSIGNED_SITE_KEY) return 1;
            if (String(siteIdB) === UNASSIGNED_SITE_KEY) return -1;
            const nameA = getSiteGroupLabel(siteIdA);
            const nameB = getSiteGroupLabel(siteIdB);
            return nameA.localeCompare(nameB);
        });
    }, [groupedCustomers, siteNameById]);

    // If the active tab's site disappeared after a regroup, fall back to All Sites
    // so we never show an empty table while recipients still exist.
    useEffect(() => {
        if (activeSiteFilter === 'all') return;
        const stillExists = sortedSiteEntries.some(([siteId]) => String(siteId) === String(activeSiteFilter));
        if (!stillExists) {
            setActiveSiteFilter('all');
        }
    }, [sortedSiteEntries, activeSiteFilter]);

    const allFilteredIds = useMemo(() =>
        (filteredCustomers || []).map(c => c.CustomerID),
    [filteredCustomers]);

    const activeCount = useMemo(
        () => (filteredCustomers || []).filter(c => c.Status?.toUpperCase() === 'ACTIVE').length,
        [filteredCustomers]
    );

    const inactiveCount = useMemo(
        () => (filteredCustomers || []).filter(c => c.Status?.toUpperCase() === 'INACTIVE').length,
        [filteredCustomers]
    );

    const displaySiteEntries = useMemo(() => {
        if (activeSiteFilter === 'all') return sortedSiteEntries;
        return sortedSiteEntries.filter(([siteId]) => String(siteId) === String(activeSiteFilter));
    }, [sortedSiteEntries, activeSiteFilter]);

    const renderStatusBadge = (status) => {
        const normalized = status?.toUpperCase();
        if (normalized === 'ACTIVE') {
            return <span className="cr-status-badge cr-status-badge--active">Active</span>;
        }
        if (normalized === 'INACTIVE') {
            return <span className="cr-status-badge cr-status-badge--inactive">Inactive</span>;
        }
        if (normalized === 'ARCHIVED') {
            return <span className="cr-status-badge cr-status-badge--archived">Archived</span>;
        }
        return <span className="cr-status-badge cr-status-badge--archived">{status || '—'}</span>;
    };

    const renderBoolIcon = (value) => (
        value
            ? <FaCheckCircle className="cr-bool-yes" />
            : <FaTimesCircle className="cr-bool-no" />
    );

    const renderSortableTh = (label, key, extraClass = '') => (
        <th
            className={`cr-th cr-th--sortable ${extraClass}`}
            onClick={() => handleSort(key)}
        >
            {label}{getSortIndicator(key)}
        </th>
    );

    const handleSelectRecipient = (id, checked) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (checked) next.add(id);
            else next.delete(id);
            return next;
        });
    };

    const handleSelectAllInSite = (siteCustomers, checked) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            siteCustomers.forEach(c => {
                if (checked) next.add(c.CustomerID);
                else next.delete(c.CustomerID);
            });
            return next;
        });
    };

    const handleSelectAll = () => setSelectedIds(new Set(allFilteredIds));
    const handleClearSelection = () => setSelectedIds(new Set());

    const handleBulkEdit = async (changes) => {
        if (Object.keys(changes).length === 0) {
            setShowBulkEditModal(false);
            return;
        }
        setIsBulkEditing(true);
        const ids = Array.from(selectedIds);
        setBulkEditProgress({ done: 0, total: ids.length });
        let succeeded = 0;
        let failed = 0;

        for (const id of ids) {
            const customer = customers.find(c => c.CustomerID === id);
            if (!customer) {
                setBulkEditProgress(prev => ({ ...prev, done: prev.done + 1 }));
                continue;
            }

            const payload = {
                customer_id: customer.CustomerID,
                name: customer.Name || '',
                surname: customer.Surname || '',
                id_no: customer.IDNo || '',
                crm_id: customer.crm_id || '',
                room_number: customer.room_number || '',
                status: changes.Status !== undefined ? changes.Status : (customer.Status || 'ACTIVE'),
                customer_care_type_id: changes.CustomerCareTypeID !== undefined
                    ? (changes.CustomerCareTypeID ? parseInt(changes.CustomerCareTypeID, 10) : null)
                    : (customer.CustomerCareTypeID ? parseInt(customer.CustomerCareTypeID, 10) : null),
                discount: changes.Discount !== undefined
                    ? (changes.Discount !== '' && changes.Discount !== null ? parseFloat(changes.Discount) : null)
                    : (customer.Discount ? parseFloat(customer.Discount) : null),
                organisational_site_id: customer.OrganisationalSiteID ? parseInt(customer.OrganisationalSiteID, 10) : null,
                mod_shared: changes.mod_shared !== undefined ? changes.mod_shared : (customer.mod_shared || false),
                mod_immobile: changes.mod_immobile !== undefined ? changes.mod_immobile : (customer.mod_immobile || false),
                mod_estate: changes.mod_estate !== undefined ? changes.mod_estate : (customer.mod_estate || false),
            };

            try {
                const response = await fetchWithTimeout(`${API_BASE_URL}/care-recipients/${id}`, {
                    method: 'PUT',
                    body: JSON.stringify(payload)
                }, 15000);
                if (response.ok) succeeded++;
                else failed++;
            } catch {
                failed++;
            }

            setBulkEditProgress(prev => ({ ...prev, done: prev.done + 1 }));
        }

        setIsBulkEditing(false);
        setShowBulkEditModal(false);
        setSelectedIds(new Set());

        if (failed > 0) {
            alert(`Bulk edit complete. ${succeeded} succeeded, ${failed} failed.`);
        }

        refreshData();
    };

    // Fetch customer detail with tenants and sites
    const fetchCustomerDetail = useCallback(async (customerId) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/care-recipients/${customerId}`);

            if (response.ok) {
                const data = await response.json();
                return {
                    ...data,
                    // Ensure arrays exist
                    tenants: data.tenants || [],
                    sites: data.sites || []
                };
            }
            return null;
        } catch (error) {
            console.error('Error fetching customer detail:', error);
            return null;
        }
    }, [fetchWithTimeout, API_BASE_URL]);

    const handleViewDetail = async (customerId) => {
        const detail = await fetchCustomerDetail(customerId);
        if (detail) {
            setSelectedCustomer(detail);
            setCustomerTenants(detail.tenants || []);
            setCustomerSites(detail.sites || []);
            setShowDetailModal(true);
        } else {
            alert('Failed to load customer details');
        }
    };

    const bindCreateToCurrentTenant = () => {
        if (!currentTenant?.id) return false;
        const tenantId = String(currentTenant.id);
        setSelectedTenantIds([tenantId]);
        setPrimaryTenantId(tenantId);
        setSelectedTenantsData([{
            id: tenantId,
            name: currentTenant.name || currentTenant.tenant_name || 'Current tenant',
        }]);
        setTenantSiteSelections((prev) => ({
            [tenantId]: Array.isArray(prev[tenantId]) ? prev[tenantId] : [],
        }));
        return true;
    };

    const openCreateModal = () => {
        if (!canCreateForCurrentTenant) return;
        bindCreateToCurrentTenant();
        setCreateStep(1);
        setShowModal(true);
    };

    const handleModalClose = () => {
        if (isCreatingCustomerRef.current) return;
        // Reset multi-step form state
        setCreateStep(1);
        setSelectedTenantIds([]);
        setSelectedTenantsData([]);
        setPrimaryTenantId(null);
        setTenantSiteSelections({});
        setExistingPerson(null);
        setPersonLookupStatus('');
        setShowModal(false);
    };

    const handleNewCustomerChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'IDNo') {
            setExistingPerson(null);
            setPersonLookupStatus('');
        }
        setNewCustomer(prevState => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleIdLookup = async () => {
        const identityNumber = (newCustomer.IDNo || '').trim();
        if (!identityNumber) {
            setExistingPerson(null);
            setPersonLookupStatus('');
            return;
        }
        setPersonLookupStatus('Looking up identity in Core…');
        try {
            const response = await fetchWithAuth(
                `${API_BASE_URL}/persons/lookup?identityNumber=${encodeURIComponent(identityNumber)}`
            );
            if (!response.ok) {
                setPersonLookupStatus('');
                return;
            }
            const data = await response.json();
            if (data?.person?.id) {
                setExistingPerson(data.person);
                setNewCustomer((prev) => ({
                    ...prev,
                    Name: data.person.name || prev.Name,
                    Surname: data.person.surname || prev.Surname,
                    IDNo: data.person.rsa_id_number || data.person.passport_no || prev.IDNo,
                }));
                setPersonLookupStatus(
                    `Using existing person: ${[data.person.name, data.person.surname].filter(Boolean).join(' ')}`
                );
            } else {
                setExistingPerson(null);
                setPersonLookupStatus('No existing Core person found. A new person will be created.');
            }
        } catch (err) {
            console.error('Person lookup failed:', err);
            setPersonLookupStatus('');
        }
    };

    const validateForm = () => {
        const errors = {};
        if (!newCustomer.Name) errors.Name = 'Name is required';
        if (!newCustomer.Surname) errors.Surname = 'Surname is required';
        if (!newCustomer.IDNo) errors.IDNo = 'ID Number is required';
        if (!newCustomer.CustomerCareTypeID) errors.CustomerCareTypeID = 'Customer Care Type is required';
        if (newCustomer.Discount === '') errors.Discount = 'Default discount is required';
        return errors;
    };

    const handleEdit = async (e) => {
        // First try to find in local state
        let customer = customers.find(cust => cust.CustomerID === e);

        // Then fetch full detail from API to get tenants and sites
        const fullDetail = await fetchCustomerDetail(e);
        if (fullDetail) {
            customer = {
                ...customer,
                ...fullDetail,
                tenants: fullDetail.tenants || [],
                sites: fullDetail.sites || []
            };
        }

        setSelectedCustomer(customer);
        setCustomerTenants(customer?.tenants || []);
        setCustomerSites(customer?.sites || []);
        setShowEditModal(true);
    };

    const handleArchiveClick = (customer) => {
        setRecipientToArchive(customer);
        setShowArchiveModal(true);
    };

    const handleArchiveConfirm = async () => {
        if (!recipientToArchive) return;

        try {
            // Convert PascalCase to snake_case for Supabase
            const updatedRecipient = {
                customer_id: recipientToArchive.CustomerID,
                name: recipientToArchive.Name || '',
                surname: recipientToArchive.Surname || '',
                id_no: recipientToArchive.IDNo || '',
                crm_id: recipientToArchive.crm_id || '',
                room_number: recipientToArchive.room_number || '',
                status: 'ARCHIVED',
                customer_care_type_id: recipientToArchive.CustomerCareTypeID ? parseInt(recipientToArchive.CustomerCareTypeID, 10) : null,
                discount: recipientToArchive.Discount ? parseFloat(recipientToArchive.Discount) : null,
                organisational_site_id: recipientToArchive.OrganisationalSiteID ? parseInt(recipientToArchive.OrganisationalSiteID, 10) : null,
                mod_shared: recipientToArchive.mod_shared || false,
                mod_immobile: recipientToArchive.mod_immobile || false,
                mod_estate: recipientToArchive.mod_estate || false
            };

            const response = await fetchWithTimeout(`${API_BASE_URL}/care-recipients/${recipientToArchive.CustomerID}`, {
                method: 'PUT',
                body: JSON.stringify(updatedRecipient)
            }, 15000); // 15 second timeout for updates

            if (response.ok) {
                // Remove the archived recipient from the list
                setCustomers(prevCustomers => prevCustomers.filter(customer => customer.CustomerID !== recipientToArchive.CustomerID));
                setShowArchiveModal(false);
                setRecipientToArchive(null);
                alert('Recipient Archived Successfully');
            } else {
                alert('Failed to archive recipient');
            }
        } catch (error) {
            console.error('Error archiving recipient:', error);
            alert('An error occurred while archiving the recipient');
        }
    };

    const handleArchiveCancel = () => {
        setShowArchiveModal(false);
        setRecipientToArchive(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setSelectedCustomer(prev => ({ ...prev, [name]: value }));
    };

    const handleEditModalClose = () => {
        setShowEditModal(false);
        setSelectedCustomer(null);
        refreshData();
    };

    const handleSaveEdit = async () => {
        if (!selectedCustomer) return;

        try {
            // Convert PascalCase to snake_case for Supabase
            const updatedCustomer = {
                customer_id: selectedCustomer.CustomerID,
                name: selectedCustomer.Name || '',
                surname: selectedCustomer.Surname || '',
                id_no: selectedCustomer.IDNo || '',
                crm_id: selectedCustomer.crm_id || '',
                room_number: selectedCustomer.room_number || '',
                status: selectedCustomer.Status || 'ACTIVE',
                customer_care_type_id: selectedCustomer.CustomerCareTypeID ? parseInt(selectedCustomer.CustomerCareTypeID, 10) : null,
                discount: selectedCustomer.Discount ? parseFloat(selectedCustomer.Discount) : null,
                organisational_site_id: selectedCustomer.OrganisationalSiteID ? parseInt(selectedCustomer.OrganisationalSiteID, 10) : null,
                mod_shared: selectedCustomer.mod_shared || false,
                mod_immobile: selectedCustomer.mod_immobile || false,
                mod_estate: selectedCustomer.mod_estate || false
            };

            const response = await fetchWithTimeout(`${API_BASE_URL}/care-recipients/${selectedCustomer.CustomerID}`, {
                method: 'PUT',
                body: JSON.stringify(updatedCustomer)
            }, 15000); // 15 second timeout for updates

            if (response.ok) {
                alert('Customer updated successfully');
                setShowEditModal(false);
                // Refresh data to ensure we have the latest information
                refreshData();
            } else {
                const errorText = await response.text();
                console.error('Failed to update customer:', errorText);
                alert('Failed to update customer');
            }
        } catch (error) {
            console.error('Error updating customer:', error);
            alert('An error occurred while updating the customer');
        }
    };

    // Multi-step form handlers
    const handleNextStep = () => {
        if (createStep !== 1) return;
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }
        if (!bindCreateToCurrentTenant()) {
            alert('Select a tenant before creating a care recipient');
            return;
        }
        setFormErrors({});
        setCreateStep(2);
    };

    const handlePrevStep = () => {
        setCreateStep(prev => Math.max(1, prev - 1));
    };

    const handleAddCustomer = async () => {
        if (isCreatingCustomerRef.current) return;
        if (!currentTenant?.id) {
            alert('Select a tenant before creating a care recipient');
            return;
        }
        if (!canCreateRecipients) {
            alert('You need Admin or Manager rights for this tenant to create a care recipient');
            return;
        }

        const tenantId = String(currentTenant.id);
        const siteIds = (tenantSiteSelections[tenantId] || [])
            .map((siteId) => String(siteId))
            .filter(Boolean);
        if (siteIds.length === 0) {
            alert('Please select at least one site for the customer');
            return;
        }

        const formattedCustomer = {
            name: newCustomer.Name || '',
            surname: newCustomer.Surname || '',
            id_no: newCustomer.IDNo || '',
            crm_id: newCustomer.crm_id || '',
            room_number: newCustomer.room_number || '',
            customer_care_type_id: parseInt(newCustomer.CustomerCareTypeID, 10),
            status: newCustomer.Status || 'ACTIVE',
            discount: newCustomer.Discount ? parseFloat(newCustomer.Discount) : null,
            mod_shared: newCustomer.mod_shared || false,
            mod_immobile: newCustomer.mod_immobile || false,
            mod_estate: newCustomer.mod_estate || false,
            tenant_id: tenantId,
            tenant_ids: [tenantId],
            is_primary_tenant_id: tenantId,
            tenant_site_assignments: { [tenantId]: siteIds },
        };

        isCreatingCustomerRef.current = true;
        setIsCreatingCustomer(true);

        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/care-recipients`, {
                method: 'POST',
                body: JSON.stringify(formattedCustomer)
            }, 60000);

            if (response.ok) {
                const newCust = await response.json();

                // Reset form state
                setCreateStep(1);
                setSelectedTenantIds([]);
                setSelectedTenantsData([]);
                setPrimaryTenantId(null);
                setTenantSiteSelections({});
                setExistingPerson(null);
                setPersonLookupStatus('');
                setNewCustomer({ Name: '', Surname: '', IDNo: '', crm_id: '', room_number: '', Status: '', CustomerCareTypeID: '', Discount: '', mod_shared: false, mod_immobile: false, mod_estate: false });

                setShowModal(false);
                alert('Customer added successfully');
                // Refresh data to ensure we have the latest information
                refreshData();
            } else {
                const errorBody = await response.json().catch(() => ({}));
                const errorMessage = errorBody.error || errorBody.message || 'Failed to add customer';
                console.error('Failed to add customer:', errorMessage);
                alert(errorMessage);
            }
        } catch (error) {
            console.error('An unexpected error occurred:', error);
            alert('An unexpected error occurred');
        } finally {
            isCreatingCustomerRef.current = false;
            setIsCreatingCustomer(false);
        }
    };

    const handleViewCarePlans = async (customerId) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/careplans/${customerId}`, {}, 15000); // 15 second timeout for care plans check

            // Check if the request was successful (status code 200-299)
            if (response.ok) {
                const data = await response.json();

                // Check if the successful response actually contains care plans (handle both camelCase and snake_case)
                let hasCarePlans = false;
                if (data.care_plans && data.care_plans.length > 0) {
                    hasCarePlans = true;
                } else if (data.carePlans && data.carePlans.length > 0) {
                    hasCarePlans = true;
                } else if (Array.isArray(data) && data.length > 0) {
                    // API returns array directly
                    hasCarePlans = true;
                }

                if (hasCarePlans) {
                    navigate(`/careplans/${customerId}`); // Navigate if plans exist
                } else {
                    // Successful response but no plans found
                    alert('No service plans found for this recipient.');
                }
            } else if (response.status === 404) {
                // Specifically handle 404 Not Found as "no plans found"
                alert('No service plans found for this recipient.');
            } else {
                // Handle other errors (500, 401, 403, etc.)
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

        } catch (error) {
            // Catch network errors or errors thrown from the 'else' block above
            console.error('Error checking for service plans:', error);
            alert('Failed to check for service plans. Please try again.'); // Keep this for genuine errors
        }
    };

    const canQuickQuote = hasPermission(PERMISSIONS.SERVICE_PLAN_CREATE) && hasPermission(PERMISSIONS.QUOTATION_CREATE);

    const handleQuickQuote = async (customer, siteId) => {
        if (!canQuickQuote) return;
        if (!siteId || String(siteId) === UNASSIGNED_SITE_KEY) {
            alert('Assign this recipient to a site before creating a quick quote.');
            return;
        }

        const siteData = customer.sites?.find(s => String(s.site_id) === String(siteId));
        const tenantId = siteData?.tenant_id || currentTenant?.id;
        if (!tenantId) {
            alert('Could not determine the tenant for this site. Try again or contact support.');
            return;
        }

        const loadingKey = `${customer.CustomerID}:${siteId}`;
        setQuickQuoteLoadingKey(loadingKey);
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/quickQuote`, {
                method: 'POST',
                body: JSON.stringify({
                    customer_id: customer.CustomerID,
                    tenant_id: tenantId,
                    site_id: siteId,
                }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                if (data.code === 'NO_DEFAULT_ACTIVITIES') {
                    setQuickQuoteExplainer(
                        data.error ||
                        'Quick Quote needs at least one default activity for this recipient’s care type. Mark activities as “Default on Service Plans” for this care type under Activities, then try again.'
                    );
                    return;
                }
                alert(data.error || data.message || 'Failed to create quick quote. Please try again.');
                return;
            }
            const carePlanId = data.care_plan_id;
            const quotationId = data.quotation_id;
            if (!carePlanId || !quotationId) {
                alert('Quick quote was created but returned an incomplete response. Refresh and open quotations for this recipient.');
                return;
            }
            navigate(`/careplans/${customer.CustomerID}/${carePlanId}/quote/${quotationId}`);
        } catch (err) {
            console.error('Quick quote failed:', err);
            alert('Failed to create quick quote. Please try again.');
        } finally {
            setQuickQuoteLoadingKey(null);
        }
    };

    return (
        <div className="care-recipients-page">
            <div className="cr-content">
                {/* ── Page hero ──────────────────────────────────────────── */}
                <div className="cr-page-hero">
                    <div className="cr-page-hero-text">
                        <h1 className="cr-page-title">Service Recipients</h1>
                        <p className="cr-page-subtitle">View and manage all service recipient information</p>
                    </div>
                    <div className="cr-stat-chips">
                        <div className="cr-stat-chip">
                            <div className="cr-stat-value cr-stat-value--primary">{filteredCustomers?.length ?? 0}</div>
                            <div className="cr-stat-label">Total</div>
                        </div>
                        <div className="cr-stat-chip">
                            <div className="cr-stat-value cr-stat-value--green">{activeCount}</div>
                            <div className="cr-stat-label">Active</div>
                        </div>
                        <div className="cr-stat-chip">
                            <div className="cr-stat-value cr-stat-value--muted">{inactiveCount}</div>
                            <div className="cr-stat-label">Inactive</div>
                        </div>
                    </div>
                </div>

                {(isLoading || isTenantLoading) && (
                    <TableSkeleton columns={12} rows={8} message="Loading service recipients…" />
                )}

                {!isLoading && !isTenantLoading && (
                    <>
                        <div className="cr-toolbar">
                            <div className="cr-toolbar-left">
                                <input
                                    type="text"
                                    className="cr-search"
                                    placeholder="Search by name, ID, CRM ID, or room..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        sessionStorage.setItem('careRecipients_search', e.target.value);
                                    }}
                                />
                            </div>
                            <div className="cr-toolbar-actions">
                                <PermissionGuard
                                    permission={PERMISSIONS.CLIENT_CREATE}
                                    showDisabled
                                    tooltip={
                                        !currentTenant?.id
                                            ? 'Select a tenant before creating a service recipient'
                                            : !canCreateRecipients
                                                ? 'You need Admin or Manager rights for this tenant to create service recipients'
                                                : 'You need permission to create Service Recipients'
                                    }
                                >
                                    <Button
                                        className="cr-btn-primary"
                                        onClick={openCreateModal}
                                        disabled={isLoadingAdditional || !canCreateForCurrentTenant}
                                        data-tooltip-id="add-care-recipient-tooltip"
                                    >
                                        <FaPlusCircle /> Add New
                                    </Button>
                                </PermissionGuard>
                                <Button
                                    className="cr-btn-outline"
                                    onClick={refreshData}
                                    disabled={isLoading || isLoadingAdditional}
                                >
                                    <FaSync className={isLoading || isLoadingAdditional ? 'fa-spin' : ''} /> Refresh
                                </Button>
                            </div>
                        </div>

                        {loadError && (
                            <div className="cr-loading-banner" role="alert" style={{ color: 'var(--theme-danger, #b42318)' }}>
                                {loadError}
                                <Button
                                    className="cr-btn-outline"
                                    size="sm"
                                    style={{ marginLeft: '0.75rem' }}
                                    onClick={refreshData}
                                >
                                    Retry
                                </Button>
                            </div>
                        )}

                        {isLoadingAdditional && (
                            <div className="cr-loading-banner">
                                <LoadingSpinner size="sm" variant="secondary" />
                                Loading additional information...
                            </div>
                        )}

                        {(sortedSiteEntries.length > 1 || sortedSiteEntries.some(([siteId]) => String(siteId) === UNASSIGNED_SITE_KEY)) && (
                            <div className="cr-site-tabs">
                                <button
                                    type="button"
                                    className={`cr-site-tab ${activeSiteFilter === 'all' ? 'cr-site-tab--active' : ''}`}
                                    onClick={() => setActiveSiteFilter('all')}
                                >
                                    All Sites
                                    <span className="cr-site-tab-count">{filteredCustomers?.length ?? 0}</span>
                                </button>
                                {sortedSiteEntries.map(([siteId, siteCustomers]) => {
                                    const siteName = getSiteGroupLabel(siteId);
                                    const isUnassigned = String(siteId) === UNASSIGNED_SITE_KEY;
                                    return (
                                        <button
                                            key={siteId}
                                            type="button"
                                            className={`cr-site-tab ${isUnassigned ? 'cr-site-tab--unassigned' : ''} ${activeSiteFilter === String(siteId) ? 'cr-site-tab--active' : ''}`}
                                            onClick={() => setActiveSiteFilter(String(siteId))}
                                        >
                                            {siteName}
                                            <span className="cr-site-tab-count">{siteCustomers.length}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {displaySiteEntries.length === 0 ? (
                            <div className="cr-table-card">
                                <div className="cr-empty-state">
                                    {loadError
                                        ? 'Unable to load service recipients'
                                        : 'No service recipients found'}
                                </div>
                            </div>
                        ) : (
                            displaySiteEntries.map(([siteId, siteCustomers]) => {
                                const siteName = getSiteGroupLabel(siteId);
                                const isUnassigned = String(siteId) === UNASSIGNED_SITE_KEY;
                                return (
                                    <div key={siteId} className={`cr-table-card ${isUnassigned ? 'cr-table-card--unassigned' : ''}`}>
                                        {activeSiteFilter === 'all' && sortedSiteEntries.length > 1 && (
                                            <div className={`cr-site-header ${isUnassigned ? 'cr-site-header--unassigned' : ''}`}>
                                                <div className="cr-site-name">
                                                    <FaBuilding className="cr-site-icon" />
                                                    {siteName}
                                                    {isUnassigned && (
                                                        <span className="cr-unassigned-badge">Tenant only</span>
                                                    )}
                                                </div>
                                                <span className="cr-site-count">{siteCustomers.length} recipient{siteCustomers.length !== 1 ? 's' : ''}</span>
                                            </div>
                                        )}
                                        {activeSiteFilter === UNASSIGNED_SITE_KEY && isUnassigned && (
                                            <div className="cr-site-header cr-site-header--unassigned">
                                                <div className="cr-site-name">
                                                    <FaBuilding className="cr-site-icon" />
                                                    {siteName}
                                                    <span className="cr-unassigned-badge">Tenant only</span>
                                                </div>
                                                <span className="cr-site-count">{siteCustomers.length} recipient{siteCustomers.length !== 1 ? 's' : ''}</span>
                                            </div>
                                        )}
                                        <div className="cr-table-scroll">
                                            <table className="cr-table">
                                                <thead>
                                                    <tr>
                                                        <th className="cr-th cr-th--check">
                                                            <Form.Check
                                                                type="checkbox"
                                                                checked={siteCustomers.length > 0 && siteCustomers.every(c => selectedIds.has(c.CustomerID))}
                                                                onChange={e => handleSelectAllInSite(siteCustomers, e.target.checked)}
                                                            />
                                                        </th>
                                                        <th className="cr-th cr-th--actions">Actions</th>
                                                        {renderSortableTh('Name', 'Name', 'cr-th')}
                                                        {renderSortableTh('Surname', 'Surname')}
                                                        <th className="cr-th">Assessment</th>
                                                        <th className="cr-th">Service Plans</th>
                                                        <th className="cr-th">Quotations</th>
                                                        {renderSortableTh('ID Number', 'IDNo')}
                                                        {renderSortableTh('CRM ID', 'crm_id')}
                                                        {renderSortableTh('Room', 'room_number')}
                                                        {renderSortableTh('Status', 'Status')}
                                                        <th className="cr-th">Shared</th>
                                                        <th className="cr-th">Immobile</th>
                                                        <th className="cr-th">Estate</th>
                                                        {renderSortableTh('Care Type', 'CustomerCareTypeID')}
                                                        {renderSortableTh('Site', 'site_id')}
                                                        {renderSortableTh('Discount %', 'Discount')}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {siteCustomers.map((customer) => (
                                                        <tr
                                                            key={customer.CustomerID}
                                                            className={selectedIds.has(customer.CustomerID) ? 'cr-row--selected' : ''}
                                                        >
                                                            <td className="cr-td" style={{ textAlign: 'center' }}>
                                                                <Form.Check
                                                                    type="checkbox"
                                                                    checked={selectedIds.has(customer.CustomerID)}
                                                                    onChange={e => handleSelectRecipient(customer.CustomerID, e.target.checked)}
                                                                />
                                                            </td>
                                                            <td className="cr-td">
                                                                <div className="cr-action-group">
                                                                    <Button variant="link" className="cr-action-btn cr-action-btn--view" data-tooltip-id={`view-tooltip-${customer.CustomerID}`} onClick={() => handleViewDetail(customer.CustomerID)}>
                                                                        <FaEye />
                                                                    </Button>
                                                                    <PermissionGuard permission={PERMISSIONS.CLIENT_UPDATE} showDisabled tooltip="You need Administrator permissions to edit Service Recipients">
                                                                        <Button variant="link" className="cr-action-btn cr-action-btn--edit" data-tooltip-id={`edit-tooltip-${customer.CustomerID}`} onClick={() => handleEdit(customer.CustomerID)}>
                                                                            <FaEdit />
                                                                        </Button>
                                                                    </PermissionGuard>
                                                                    <PermissionGuard permission={PERMISSIONS.CLIENT_ARCHIVE} showDisabled tooltip="You do not have permission to archive service recipients">
                                                                        <Button variant="link" className="cr-action-btn cr-action-btn--archive" data-tooltip-id={`archive-tooltip-${customer.CustomerID}`} onClick={() => handleArchiveClick(customer)}>
                                                                            <FaArchive />
                                                                        </Button>
                                                                    </PermissionGuard>
                                                                </div>
                                                            </td>
                                                            <td className="cr-td">
                                                                <div className="cr-recipient-name">{customer.Name}</div>
                                                            </td>
                                                            <td className="cr-td">{customer.Surname}</td>
                                                            <td className="cr-td">
                                                                <div className="cr-action-group">
                                                                    <Button variant="link" className="cr-action-btn cr-action-btn--assessment" data-tooltip-id={`assessment-tooltip-${customer.CustomerID}`} onClick={() => handleAssessmentModalOpen(customer)}>
                                                                        <FaFilePdf />
                                                                    </Button>
                                                                    {assessmentExists[customer.CustomerID] && (
                                                                        <PermissionGuard permission={PERMISSIONS.ASSESSMENT_READ} showDisabled tooltip="You need Assessment Read permissions to download assessments">
                                                                            <Button variant="link" className="cr-action-btn cr-action-btn--download" data-tooltip-id={`download-assessment-tooltip-${customer.CustomerID}`} onClick={() => handleAssessmentDownload(customer.CustomerID)}>
                                                                                <FaDownload />
                                                                            </Button>
                                                                        </PermissionGuard>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="cr-td">
                                                                <div className="cr-action-group">
                                                                    {(() => {
                                                                        const currentSiteId = siteId;
                                                                        const siteData = customer.sites?.find(s => String(s.site_id) === String(currentSiteId));
                                                                        const tenantId = siteData?.tenant_id || currentTenant?.id;
                                                                        const params = new URLSearchParams();
                                                                        if (tenantId) params.append('tenantId', tenantId);
                                                                        if (currentSiteId) params.append('siteId', currentSiteId);
                                                                        const queryString = params.toString();
                                                                        const linkUrl = `/careplans/${customer.CustomerID}/new${queryString ? '?' + queryString : ''}`;
                                                                        return (
                                                                            <PermissionGuard permission={PERMISSIONS.SERVICE_PLAN_CREATE} showDisabled tooltip="You need Administrator permissions to create Service Plans">
                                                                                <Button variant="link" className="cr-action-btn cr-action-btn--add" data-tooltip-id={`add-care-tooltip-${customer.CustomerID}`} as={Link} to={linkUrl}>
                                                                                    <FaPlusCircle />
                                                                                </Button>
                                                                            </PermissionGuard>
                                                                        );
                                                                    })()}
                                                                    <Button variant="link" className="cr-action-btn cr-action-btn--plans" data-tooltip-id={`view-care-tooltip-${customer.CustomerID}`} onClick={() => handleViewCarePlans(customer.CustomerID)}>
                                                                        <FaEye />
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                            <td className="cr-td">
                                                                <div className="cr-action-group">
                                                                    {(() => {
                                                                        const currentSiteId = siteId;
                                                                        const isUnassignedSite = String(currentSiteId) === UNASSIGNED_SITE_KEY;
                                                                        const loadingKey = `${customer.CustomerID}:${currentSiteId}`;
                                                                        const isLoadingQuick = quickQuoteLoadingKey === loadingKey;
                                                                        return (
                                                                            <PermissionGuard
                                                                                permission={PERMISSIONS.QUOTATION_CREATE}
                                                                                showDisabled
                                                                                tooltip={!canQuickQuote
                                                                                    ? 'You need Service Plan Create and Quotation Create permissions for Quick Quote'
                                                                                    : 'You need Quotation Create permission for Quick Quote'}
                                                                            >
                                                                                <Button
                                                                                    variant="link"
                                                                                    className="cr-action-btn cr-action-btn--quick-quote"
                                                                                    data-tooltip-id={`quick-quote-tooltip-${customer.CustomerID}`}
                                                                                    disabled={!canQuickQuote || isUnassignedSite || !!quickQuoteLoadingKey}
                                                                                    onClick={() => handleQuickQuote(customer, currentSiteId)}
                                                                                >
                                                                                    {isLoadingQuick ? <LoadingSpinner size="sm" variant="secondary" /> : <FaPlusCircle />}
                                                                                </Button>
                                                                            </PermissionGuard>
                                                                        );
                                                                    })()}
                                                                    <Button variant="link" className="cr-action-btn cr-action-btn--quotes" data-tooltip-id={`view-quote-tooltip-${customer.CustomerID}`} as={Link} to={`/quotations/${customer.CustomerID}`}>
                                                                        <FaFileInvoice />
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                            <td className="cr-td">{customer.IDNo}</td>
                                                            <td className="cr-td">{customer.crm_id}</td>
                                                            <td className="cr-td">{customer.room_number || '—'}</td>
                                                            <td className="cr-td">{renderStatusBadge(customer.Status)}</td>
                                                            <td className="cr-td">
                                                                <span data-tooltip-id={`shared-tooltip-${customer.CustomerID}`}>{renderBoolIcon(customer.mod_shared)}</span>
                                                            </td>
                                                            <td className="cr-td">
                                                                <span data-tooltip-id={`immobile-tooltip-${customer.CustomerID}`}>{renderBoolIcon(customer.mod_immobile)}</span>
                                                            </td>
                                                            <td className="cr-td">
                                                                <span data-tooltip-id={`estate-tooltip-${customer.CustomerID}`}>{renderBoolIcon(customer.mod_estate)}</span>
                                                            </td>
                                                            <td className="cr-td">
                                                                {isLoadingAdditional ? (
                                                                    <span className="text-muted">Loading...</span>
                                                                ) : (
                                                                    customerCareTypes.find((item) => item.CareTypeID == customer.CustomerCareTypeID)?.Name || '—'
                                                                )}
                                                            </td>
                                                            <td className="cr-td">
                                                                {isLoadingAdditional ? (
                                                                    <span className="text-muted">Loading...</span>
                                                                ) : (
                                                                    (() => {
                                                                        const customerSites = customer.sites || [];
                                                                        const tenantFilteredSites = currentTenant
                                                                            ? customerSites.filter(s => String(s.tenant_id) === String(currentTenant.id))
                                                                            : customerSites;
                                                                        if (tenantFilteredSites.length > 1) {
                                                                            return tenantFilteredSites.map(s => s.site_name).join(', ');
                                                                        }
                                                                        if (tenantFilteredSites.length === 1) {
                                                                            return tenantFilteredSites[0].site_name;
                                                                        }
                                                                        return sites.find((item) => item.SiteID == customer.site_id)?.SiteName || '—';
                                                                    })()
                                                                )}
                                                            </td>
                                                            <td className="cr-td">{customer.Discount ?? '—'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                );
                            })
                        )}

                        <ReactTooltip style={{ zIndex: 1000 }} isMulti selector="[data-tooltip-id^='view-tooltip-']" place="top" content="View Details" />
                        <ReactTooltip style={{ zIndex: 1000 }} isMulti selector="[data-tooltip-id^='edit-tooltip-']" place="top" content="Edit Service Recipient" />
                        <ReactTooltip style={{ zIndex: 1000 }} isMulti selector="[data-tooltip-id^='archive-tooltip-']" place="top" content="Archive Service Recipient" />
                        <ReactTooltip isMulti selector="[data-tooltip-id^='assessment-tooltip-']" place="top" content="Manage Assessment" />
                        <ReactTooltip isMulti selector="[data-tooltip-id^='download-assessment-tooltip-']" place="top" content="Download Assessment" />
                        <ReactTooltip style={{ zIndex: 1000 }} isMulti selector="[data-tooltip-id^='add-care-tooltip-']" place="top" content="Add Service Plan" />
                        <ReactTooltip style={{ zIndex: 1000 }} isMulti selector="[data-tooltip-id^='view-care-tooltip-']" place="top" content="View Service Plans" />
                        <ReactTooltip style={{ zIndex: 1000 }} isMulti selector="[data-tooltip-id^='quick-quote-tooltip-']" place="top" content="Quick Quote" />
                        <ReactTooltip style={{ zIndex: 1000 }} isMulti selector="[data-tooltip-id^='view-quote-tooltip-']" place="top" content="View Quotations" />
                        <ReactTooltip isMulti selector="[data-tooltip-id^='shared-tooltip-']" place="top" content="Shared accommodation" />
                        <ReactTooltip isMulti selector="[data-tooltip-id^='immobile-tooltip-']" place="top" content="Immobile" />
                        <ReactTooltip isMulti selector="[data-tooltip-id^='estate-tooltip-']" place="top" content="Estate" />
                        <ReactTooltip id="add-care-recipient-tooltip" place="top">
                            {!currentTenant?.id
                                ? 'Select a tenant before creating a service recipient'
                                : !canCreateRecipients
                                    ? 'You need Admin or Manager rights for this tenant to create service recipients'
                                    : hasPermission(PERMISSIONS.CLIENT_CREATE)
                                        ? 'Add New Service Recipient'
                                        : 'You need permission to create Service Recipients'}
                        </ReactTooltip>
                        <ReactTooltip id="edit-tooltip" place="top">
                            {hasPermission(PERMISSIONS.CLIENT_UPDATE) ? 'Edit Service Recipient' : 'You need Administrator permissions to edit Service Recipients'}
                        </ReactTooltip>
                        <ReactTooltip id="archive-tooltip" place="top">
                            {hasPermission(PERMISSIONS.CLIENT_ARCHIVE) ? 'Archive Service Recipient' : 'You do not have permission to archive service recipients'}
                        </ReactTooltip>
                        <ReactTooltip id="add-service-plan-tooltip" place="top">
                            {hasPermission(PERMISSIONS.SERVICE_PLAN_CREATE) ? 'Add Service Plan' : 'You need Administrator permissions to create Service Plans'}
                        </ReactTooltip>
                    </>
                )}
            </div>

            <Modal
                show={showModal}
                onHide={handleModalClose}
                size="lg"
                backdrop={isCreatingCustomer ? 'static' : true}
                keyboard={!isCreatingCustomer}
            >
                <Modal.Header closeButton={!isCreatingCustomer}>
                    <Modal.Title>
                        Add New Customer - Step {createStep} of 2
                        {currentTenant?.name ? ` (${currentTenant.name})` : ''}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ position: 'relative' }}>
                    {isCreatingCustomer && (
                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                background: 'rgba(255, 255, 255, 0.8)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 5,
                            }}
                        >
                            <LoadingSpinner message="Creating care recipient…" />
                        </div>
                    )}
                    {/* Progress Bar */}
                    <ProgressBar
                        now={(createStep / 2) * 100}
                        variant="success"
                        className="mb-4"
                        style={{ height: '8px' }}
                    />

                    {/* Step 1: Basic Info */}
                    {createStep === 1 && (
                        <Form>
                            <h5 className="mb-3">Basic Information</h5>
                            <Form.Group controlId="newCustomerName">
                                <Form.Label>Name *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="Name"
                                    value={newCustomer.Name}
                                    onChange={handleNewCustomerChange}
                                    readOnly={Boolean(existingPerson)}
                                    isInvalid={!!formErrors.Name}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formErrors.Name}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group controlId="newCustomerSurname">
                                <Form.Label>Surname *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="Surname"
                                    value={newCustomer.Surname}
                                    onChange={handleNewCustomerChange}
                                    readOnly={Boolean(existingPerson)}
                                    isInvalid={!!formErrors.Surname}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formErrors.Surname}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group controlId="newCustomerIDNo">
                                <Form.Label>ID Number *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="IDNo"
                                    value={newCustomer.IDNo}
                                    onChange={handleNewCustomerChange}
                                    onBlur={handleIdLookup}
                                    isInvalid={!!formErrors.IDNo}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formErrors.IDNo}
                                </Form.Control.Feedback>
                                {personLookupStatus && (
                                    <Form.Text className={existingPerson ? 'text-success' : 'text-muted'}>
                                        {personLookupStatus}
                                    </Form.Text>
                                )}
                            </Form.Group>

                            <Form.Group controlId="newCustomerCrmId">
                                <Form.Label>CRM ID</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="crm_id"
                                    value={newCustomer.crm_id}
                                    onChange={handleNewCustomerChange}
                                />
                            </Form.Group>

                            <Form.Group controlId="newCustomerRoomNumber">
                                <Form.Label>Room Number</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="room_number"
                                    value={newCustomer.room_number}
                                    onChange={handleNewCustomerChange}
                                />
                            </Form.Group>

                            <Form.Group controlId="newCustomerCustomerCareTypeID">
                                <Form.Label>Care Type *</Form.Label>
                                <Form.Control
                                    as="select"
                                    name="CustomerCareTypeID"
                                    value={newCustomer.CustomerCareTypeID}
                                    onChange={handleNewCustomerChange}
                                    isInvalid={!!formErrors.CustomerCareTypeID}
                                >
                                    <option value="">Select a Care Type</option>
                                    {customerCareTypes.map((careType) => (
                                        <option key={careType.CareTypeID} value={careType.CareTypeID}>
                                            {careType.Name}
                                        </option>
                                    ))}
                                </Form.Control>
                                <Form.Control.Feedback type="invalid">
                                    {formErrors.CustomerCareTypeID}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group controlId="newCustomerDefaultDiscount">
                                <Form.Label>Default Discount Percentage</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="Discount"
                                    value={newCustomer.Discount}
                                    onChange={handleNewCustomerChange}
                                    isInvalid={!!formErrors.Discount}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formErrors.Discount}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group controlId="newCustomerModShared">
                                <Form.Check
                                    type="checkbox"
                                    label="Mod Shared"
                                    name="mod_shared"
                                    checked={newCustomer.mod_shared}
                                    onChange={handleNewCustomerChange}
                                />
                            </Form.Group>
                            <Form.Group controlId="newCustomerModImmobile">
                                <Form.Check
                                    type="checkbox"
                                    label="Mod Immobile"
                                    name="mod_immobile"
                                    checked={newCustomer.mod_immobile}
                                    onChange={handleNewCustomerChange}
                                />
                            </Form.Group>
                            <Form.Group controlId="newCustomerModEstate">
                                <Form.Check
                                    type="checkbox"
                                    label="Mod Estate"
                                    name="mod_estate"
                                    checked={newCustomer.mod_estate}
                                    onChange={handleNewCustomerChange}
                                />
                            </Form.Group>
                        </Form>
                    )}

                    {/* Step 2: Sites for the current tenant */}
                    {createStep === 2 && (
                        <div>
                            <h5 className="mb-3">Select Sites</h5>
                            <p className="text-muted mb-3">
                                This care recipient will be created in{' '}
                                <strong>{currentTenant?.name || 'the current tenant'}</strong>.
                                Choose at least one site.
                            </p>

                            {(!currentTenant?.id || selectedTenantIds.length === 0) ? (
                                <div className="alert alert-warning">
                                    Select a tenant in the header before creating a care recipient.
                                </div>
                            ) : (
                                selectedTenantIds.map(tenantId => {
                                    // Keep tenantId as string (UUID), don't convert to number
                                    const tenantIdStr = String(tenantId);
                                    // Use selectedTenantsData first (from TenantSelector), fallback to allTenants
                                    // Use string comparison for UUID matching
                                    const tenant = selectedTenantsData.find(t =>
                                        String(t.id) === tenantIdStr
                                    ) || allTenants.find(t =>
                                        String(t.id) === tenantIdStr || String(t.TenantID) === tenantIdStr
                                    );
                                    // Use the correct field name - API returns 'name' not 'tenant_name' for /my-tenants
                                    const tenantName = tenant?.name || tenant?.TenantName || currentTenant?.name || `Tenant ${tenantId}`;
                                    return (
                                        <Card key={tenantId} className="mb-3">
                                            <Card.Header>
                                                <FaBuilding className="mr-2" />
                                                <strong>{tenantName}</strong>
                                                <Badge variant="primary" className="ml-2">
                                                    Current tenant
                                                </Badge>
                                            </Card.Header>
                                            <Card.Body>
                                                <SiteSelector
                                                    key={`site-selector-${tenantId}`}
                                                    tenantId={tenantIdStr}
                                                    selectedIds={tenantSiteSelections[tenantId] || []}
                                                    onChange={(siteIds) => setTenantSiteSelections({
                                                        [tenantId]: siteIds
                                                    })}
                                                    multiple={true}
                                                />
                                            </Card.Body>
                                        </Card>
                                    );
                                })
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleModalClose} disabled={isCreatingCustomer}>
                        Cancel
                    </Button>

                    {createStep > 1 && (
                        <Button variant="outline-primary" onClick={handlePrevStep} disabled={isCreatingCustomer}>
                            Previous
                        </Button>
                    )}

                    {createStep < 2 ? (
                        <Button variant="primary" onClick={handleNextStep}>
                            Next
                        </Button>
                    ) : (
                        <Button variant="success" onClick={handleAddCustomer} disabled={isCreatingCustomer}>
                            {isCreatingCustomer
                                ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Creating…</>
                                : 'Create Customer'}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
            <Modal show={showEditModal} onHide={handleEditModalClose} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Edit Customer</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {/* Basic Info Section - Always visible */}
                    <Card className="mb-3">
                        <Card.Header>
                            <strong><FaBuilding className="mr-2" />Basic Information</strong>
                        </Card.Header>
                        <Card.Body>
                            <Form>
                                <Form.Group controlId="editCustomerName">
                                    <Form.Label>Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={selectedCustomer?.Name}
                                        onChange={(e) => setSelectedCustomer({ ...selectedCustomer, Name: e.target.value })}
                                    />
                                </Form.Group>
                                <Form.Group controlId="editCustomerSurname">
                                    <Form.Label>Surname</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={selectedCustomer?.Surname}
                                        onChange={(e) => setSelectedCustomer({ ...selectedCustomer, Surname: e.target.value })}
                                    />
                                </Form.Group>
                                <Form.Group controlId="editCustomerIDNo">
                                    <Form.Label>ID Number</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={selectedCustomer?.IDNo}
                                        onChange={(e) => setSelectedCustomer({ ...selectedCustomer, IDNo: e.target.value })}
                                    />
                                </Form.Group>
                                <Form.Group controlId="editCustomerCrmId">
                                    <Form.Label>CRM ID</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={selectedCustomer?.crm_id || ''}
                                        onChange={(e) => setSelectedCustomer({ ...selectedCustomer, crm_id: e.target.value })}
                                    />
                                </Form.Group>
                                <Form.Group controlId="editCustomerRoomNumber">
                                    <Form.Label>Room Number</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={selectedCustomer?.room_number || ''}
                                        onChange={(e) => setSelectedCustomer({ ...selectedCustomer, room_number: e.target.value })}
                                    />
                                </Form.Group>
                                <Form.Group controlId="editCustomerStatus">
                                    <Form.Label>Status</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={selectedCustomer?.Status || ''}
                                        onChange={(e) => setSelectedCustomer({ ...selectedCustomer, Status: e.target.value })}
                                    >
                                        <option value="ACTIVE">ACTIVE</option>
                                        <option value="INACTIVE">INACTIVE</option>
                                    </Form.Control>
                                </Form.Group>
                                <Form.Group controlId="CustomerCareTypeID">
                                    <Form.Label>Care Type</Form.Label>
                                    <Form.Control
                                        as="select"
                                        name="CustomerCareTypeID"
                                        value={selectedCustomer?.CustomerCareTypeID || ''}
                                        onChange={handleCareTypeChange}
                                    >
                                        <option value="">Select a Care Type</option>
                                        {customerCareTypes.map((careType) => (
                                            <option key={careType.CareTypeID} value={careType.CareTypeID}>
                                                {careType.Name}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>
                                <Form.Group controlId="editCustomerDiscount">
                                    <Form.Label>Default Discount Percentage</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={selectedCustomer?.Discount || ''}
                                        onChange={(e) => setSelectedCustomer({ ...selectedCustomer, Discount: e.target.value })}
                                    />
                                </Form.Group>
                                <Form.Group controlId="editCustomerModShared">
                                    <Form.Check
                                        type="checkbox"
                                        label="Mod Shared"
                                        name="mod_shared"
                                        checked={selectedCustomer?.mod_shared}
                                        onChange={(e) => setSelectedCustomer({ ...selectedCustomer, mod_shared: e.target.checked })}
                                    />
                                </Form.Group>
                                <Form.Group controlId="editCustomerModImmobile">
                                    <Form.Check
                                        type="checkbox"
                                        label="Mod Immobile"
                                        name="mod_immobile"
                                        checked={selectedCustomer?.mod_immobile}
                                        onChange={(e) => setSelectedCustomer({ ...selectedCustomer, mod_immobile: e.target.checked })}
                                    />
                                </Form.Group>
                                <Form.Group controlId="editCustomerModEstate">
                                    <Form.Check
                                        type="checkbox"
                                        label="Mod Estate"
                                        name="mod_estate"
                                        checked={selectedCustomer?.mod_estate}
                                        onChange={(e) => setSelectedCustomer({ ...selectedCustomer, mod_estate: e.target.checked })}
                                    />
                                </Form.Group>
                            </Form>
                        </Card.Body>
                    </Card>

                    {/* Tenant Management Section */}
                    <Card className="mb-3">
                        <Card.Header>
                            <strong><FaBuilding className="mr-2" />Tenant Management ({customerTenants.length})</strong>
                        </Card.Header>
                        <Card.Body>
                            <CustomerTenantList
                                customerId={selectedCustomer?.CustomerID}
                                tenants={customerTenants}
                                onUpdate={(updatedTenants) => {
                                    setCustomerTenants(updatedTenants || []);
                                }}
                                readOnly={false}
                            />
                        </Card.Body>
                    </Card>

                    {/* Site Management Section */}
                    <Card className="mb-3">
                        <Card.Header>
                            <strong><FaBuilding className="mr-2" />Site Management ({customerSites.length})</strong>
                        </Card.Header>
                        <Card.Body>
                            <CustomerSiteList
                                customerId={selectedCustomer?.CustomerID}
                                sites={customerSites}
                                tenants={allTenants}
                                onUpdate={(updatedSites) => {
                                    const nextSites = updatedSites || [];
                                    setCustomerSites(nextSites);
                                    if (selectedCustomer?.CustomerID) {
                                        setCustomers(prev => prev.map(c =>
                                            c.CustomerID === selectedCustomer.CustomerID
                                                ? { ...c, sites: nextSites }
                                                : c
                                        ));
                                    }
                                }}
                                readOnly={false}
                            />
                        </Card.Body>
                    </Card>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleEditModalClose}>Close</Button>
                    <Button variant="primary" onClick={handleSaveEdit}>Save Changes</Button>
                </Modal.Footer>
            </Modal>

            {/* Customer Detail Modal */}
            <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaUser className="mr-2" />
                        Customer Details - {selectedCustomer?.name || selectedCustomer?.Name} {selectedCustomer?.surname || selectedCustomer?.Surname}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedCustomer && (
                        <div>
                            {/* Basic Info Card */}
                            <Card className="mb-3">
                                <Card.Header><strong>Basic Information</strong></Card.Header>
                                <Card.Body>
                                    <Row>
                                        <Col md={6}>
                                            <p><strong>Name:</strong> {selectedCustomer.name || selectedCustomer.Name}</p>
                                            <p><strong>Surname:</strong> {selectedCustomer.surname || selectedCustomer.Surname}</p>
                                            <p><strong>ID Number:</strong> {selectedCustomer.id_no || selectedCustomer.IDNo || 'N/A'}</p>
                                        </Col>
                                        <Col md={6}>
                                            <p><strong>CRM ID:</strong> {selectedCustomer.crm_id || 'N/A'}</p>
                                            <p><strong>Room Number:</strong> {selectedCustomer.room_number || selectedCustomer.room_number || 'N/A'}</p>
                                            <p><strong>Status:</strong> <Badge variant={selectedCustomer.status === 'ACTIVE' ? 'success' : 'warning'}>{selectedCustomer.status || selectedCustomer.Status}</Badge></p>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            {/* Tenants Card */}
                            <Card className="mb-3">
                                <Card.Header>
                                    <FaBuilding className="mr-2" />
                                    <strong>Tenants ({customerTenants.length})</strong>
                                </Card.Header>
                                <Card.Body>
                                    {customerTenants.length === 0 ? (
                                        <p className="text-muted">No tenants assigned</p>
                                    ) : (
                                        <Table striped bordered size="sm" className="mb-0">
                                            <thead>
                                                <tr>
                                                    <th style={{ minWidth: '200px' }}>Tenant Name</th>
                                                    <th style={{ width: '80px', whiteSpace: 'nowrap' }}>Primary</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {customerTenants.map((tenant, idx) => (
                                                    <tr key={tenant.tenant_id || idx}>
                                                        <td>{tenant.tenant_name}</td>
                                                        <td className="text-center">{tenant.is_primary ? <FaStar style={{ color: '#ffc107' }} /> : '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    )}
                                </Card.Body>
                            </Card>

                            {/* Sites Card */}
                            <Card className="mb-3">
                                <Card.Header>
                                    <FaBuilding className="mr-2" />
                                    <strong>Sites ({customerSites.length})</strong>
                                </Card.Header>
                                <Card.Body>
                                    {customerSites.length === 0 ? (
                                        <p className="text-muted">No sites assigned</p>
                                    ) : (
                                        <Table striped bordered size="sm" className="mb-0">
                                            <thead>
                                                <tr>
                                                    <th style={{ minWidth: '200px' }}>Site Name</th>
                                                    <th style={{ minWidth: '200px' }}>Tenant</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {customerSites.map((site, idx) => (
                                                    <tr key={site.tenant_site_id || idx}>
                                                        <td>{site.site_name}</td>
                                                        <td>{site.tenant_name}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    )}
                                </Card.Body>
                            </Card>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDetailModal(false)}>Close</Button>
                    <PermissionGuard
                        permission={PERMISSIONS.CLIENT_UPDATE}
                        showDisabled
                        tooltip="You do not have permission to edit service recipients"
                    >
                        <Button variant="primary" onClick={() => {
                            setShowDetailModal(false);
                            handleEdit(selectedCustomer?.customer_id || selectedCustomer?.CustomerID);
                        }}>
                            <FaEdit className="mr-1" /> Edit Customer
                        </Button>
                    </PermissionGuard>
                </Modal.Footer>
            </Modal>

            {/* Assessment Modal */}
            <Modal show={showAssessmentModal} onHide={handleAssessmentModalClose}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        Assessment Management - {selectedCustomerForAssessment?.Name} {selectedCustomerForAssessment?.Surname}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedCustomerForAssessment && (
                        <div>
                            <p><strong>Customer:</strong> {selectedCustomerForAssessment.Name} {selectedCustomerForAssessment.Surname}</p>
                            <p><strong>ID Number:</strong> {selectedCustomerForAssessment.IDNo}</p>

                            {assessmentExists[selectedCustomerForAssessment.CustomerID] ? (
                                <div className="alert alert-info">
                                    <FaFilePdf style={{ marginRight: '8px' }} />
                                    An assessment document already exists for this customer.
                                </div>
                            ) : (
                                <div className="alert alert-warning">
                                    <FaUpload style={{ marginRight: '8px' }} />
                                    No assessment document found. Please upload one.
                                </div>
                            )}

                            <Form.Group controlId="assessmentFile">
                                <Form.Label>Upload Assessment PDF</Form.Label>
                                <Form.Control
                                    type="file"
                                    accept=".pdf"
                                    onChange={handleFileChange}
                                    isInvalid={!assessmentFile && showAssessmentModal}
                                />
                                <Form.Text className="text-muted">
                                    Only PDF files are allowed. Maximum file size: 10MB
                                </Form.Text>
                            </Form.Group>

                            {assessmentFile && (
                                <div className="mt-3">
                                    <p><strong>Selected file:</strong> {assessmentFile.name}</p>
                                    <p><strong>File size:</strong> {(assessmentFile.size / 1024 / 1024).toFixed(2)} MB</p>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleAssessmentModalClose}>
                        Close
                    </Button>
                    {assessmentExists[selectedCustomerForAssessment?.CustomerID] && (
                        <Button
                            variant="success"
                            onClick={() => handleAssessmentDownload(selectedCustomerForAssessment?.CustomerID)}
                        >
                            <FaDownload style={{ marginRight: '8px' }} />
                            Download Existing Assessment
                        </Button>
                    )}
                    <PermissionGuard
                                            permission={PERMISSIONS.ASSESSMENT_CREATE}
                                            showDisabled
                                            tooltip="You need Assessment Create permissions to upload assessments"
                                        >
                                            <LoadingButton
                                                variant="primary"
                                                onClick={handleAssessmentUploadWithPermission}
                                                disabled={!assessmentFile}
                                                isLoading={uploadingAssessment}
                                                loadingText="Uploading..."
                                            >
                                                <FaUpload style={{ marginRight: '8px' }} />
                                                Upload Assessment
                                            </LoadingButton>
                                        </PermissionGuard>
                </Modal.Footer>
            </Modal>

            {/* Archive Confirmation Modal */}
            <Modal show={showArchiveModal} onHide={handleArchiveCancel}>
                <Modal.Header closeButton>
                    <Modal.Title>Archive Service Recipient</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to archive {recipientToArchive?.Name} {recipientToArchive?.Surname}?
                    <br /><br />
                    This action will change their status to ARCHIVED and remove them from the screen.
                    <br /><br />
                    This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleArchiveCancel}>
                        Cancel
                    </Button>
                    <Button variant="warning" onClick={handleArchiveConfirm}>
                        Archive
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={!!quickQuoteExplainer} onHide={() => setQuickQuoteExplainer(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Quick Quote unavailable</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ marginBottom: 0 }}>{quickQuoteExplainer}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setQuickQuoteExplainer(null)}>
                        Got it
                    </Button>
                </Modal.Footer>
            </Modal>

            <BulkEditCareRecipientsModal
                show={showBulkEditModal}
                onHide={() => setShowBulkEditModal(false)}
                selectedCount={selectedIds.size}
                customerCareTypes={customerCareTypes}
                onApply={handleBulkEdit}
                isApplying={isBulkEditing}
                progress={bulkEditProgress}
            />

            {selectedIds.size > 0 && (
                <div className="cr-bulk-bar">
                    <span style={{ fontWeight: 600 }}>{selectedIds.size} selected</span>
                    <span style={{ borderLeft: '1px solid rgba(255,255,255,0.3)', alignSelf: 'stretch' }} />
                    <Button
                        variant="link"
                        style={{ padding: 0, fontSize: '13px', textDecoration: 'underline' }}
                        onClick={handleSelectAll}
                    >
                        Select all ({allFilteredIds.length})
                    </Button>
                    <span style={{ borderLeft: '1px solid rgba(255,255,255,0.3)', alignSelf: 'stretch' }} />
                    <PermissionGuard
                        permission={PERMISSIONS.CLIENT_UPDATE}
                        showDisabled
                        tooltip="You need Administrator permissions to bulk edit Service Recipients"
                    >
                        <Button
                            variant="light"
                            size="sm"
                            onClick={() => setShowBulkEditModal(true)}
                        >
                            Edit fields
                        </Button>
                    </PermissionGuard>
                    <Button
                        variant="link"
                        style={{ color: 'rgba(255,255,255,0.6)', padding: 0, fontSize: '16px', lineHeight: 1 }}
                        onClick={handleClearSelection}
                    >
                        <FaTimesCircle />
                    </Button>
                </div>
            )}
        </div>
    );
}

export default CareRecipients;
